import React, { useEffect, useState } from 'react';
import { FilterCriteria, InventoryGroupBy, InventoryGroupRow, Page } from '@ampsec/platform-client';
import { getGridData } from 'Apis/library';
import { StatTile } from 'Components/atoms/StatTile';

/**
 * Tenant-wide totals for each grouping dimension. Each is the server-reported
 * `hints.count` from a `limit: 1` grouped request, so the tiles show true totals
 * (not sums over the API-capped grid page) and stay stable across mode switches.
 */
interface DimensionTotals {
    software?: number;
    source?: number;
    asset?: number;
}

const fetchCount = async (groupBy: InventoryGroupBy): Promise<number | undefined> => {
    const filter: FilterCriteria = { groupBy, limit: 1 };
    const page = (await getGridData('deviceSoftwareInventory', filter, 'grouped')) as Page<InventoryGroupRow>;
    return typeof page?.hints?.count === 'number' ? page.hints.count : undefined;
};

const formatCount = (value: number | undefined): string => (typeof value === 'number' ? value.toLocaleString() : '—');

/** The KPI tiles above the inventory grid: one true total per grouping dimension. */
const InventoryStatRow: React.FC = () => {
    const [totals, setTotals] = useState<DimensionTotals>({});

    useEffect(() => {
        let active = true;
        (async () => {
            const [software, source, asset] = await Promise.all([
                fetchCount('software'),
                fetchCount('source'),
                fetchCount('asset'),
            ]);
            if (active) {
                setTotals({ software, source, asset });
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    return (
        <div className="inventory-stat-row">
            <StatTile label="Software titles" value={formatCount(totals.software)} />
            <StatTile label="Devices" value={formatCount(totals.asset)} />
            <StatTile label="Sources" value={formatCount(totals.source)} />
            <StatTile label="CVEs" value="—" hint="Matching planned" muted />
        </div>
    );
};

export default InventoryStatRow;
